import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Download, Radio, ShieldOff, Trophy } from "lucide-react";
import { api, errMsg, useApi } from "../lib/api";
import { fmtCoords } from "../lib/labels";
import { PageHeader } from "../components/Layout";
import { ConfirmDialog, Countdown, PauseNote, Stat, StatusBadge } from "../components/Common";
import { ActiveCaptures } from "../components/ActiveCaptures";

const WarCard = ({ war, onCancel }) => (
  <div className={`panel fade-up p-5 ${war.status === "fight" ? "border-rose-500/40" : "border-amber-500/30"}`} data-testid={`active-war-card-${war.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Zona</div>
        <div className="font-display text-lg font-semibold">{war.zone?.name}</div>
      </div>
      <StatusBadge status={war.status} testid={`active-war-status-${war.id}`} />
    </div>
    <div className="mt-4 flex items-center gap-3 text-sm">
      <span className="font-semibold text-rose-300">{war.attacker?.label}</span>
      <span className="font-mono text-xs text-slate-600">VS</span>
      <span className="font-semibold text-cyan-300">{war.defender?.label || "Zona livre"}</span>
    </div>
    <div className="mt-5 flex items-end justify-between">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
          {war.status === "negotiation" ? "Combate começa em" : "Combate termina em"}
        </div>
        <Countdown war={war} testid={`war-countdown-${war.id}`} className="font-mono text-3xl font-bold" />
        <div className="mt-1"><PauseNote war={war} testid={`war-pause-${war.id}`} /></div>
        <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500" data-testid={`war-presence-${war.id}`}>
          Na zona: <span className="text-rose-300">{war.attacker_inside}</span> / <span className="text-cyan-300">{war.defender_inside}</span>
        </div>
      </div>
      <div className="text-right">
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Kills</div>
        <div className="font-mono text-xl" data-testid={`active-war-kills-${war.id}`}>
          <span className="text-rose-300">{war.attacker_kills}</span><span className="text-slate-600"> : </span><span className="text-cyan-300">{war.defender_kills}</span>
        </div>
      </div>
    </div>
    <button onClick={() => onCancel(war)} data-testid={`active-war-cancel-${war.id}`}
      className="mt-5 flex w-full items-center justify-center gap-2 border border-slate-800 py-2 font-mono text-[11px] uppercase tracking-widest text-slate-500 transition-colors hover:border-rose-500/50 hover:text-rose-400">
      <ShieldOff className="h-3.5 w-3.5" />Cancelar guerra
    </button>
  </div>
);

const TopRanking = ({ ranking }) => (
  <div className="panel p-5" data-testid="dashboard-top-ranking">
    <div className="mb-4 flex items-center justify-between">
      <h2 className="flex items-center gap-2 text-lg font-semibold uppercase tracking-wider"><Trophy className="h-4 w-4 text-amber-400" />Top 5</h2>
      <Link to="/ranking" target="_blank" className="font-mono text-[11px] uppercase tracking-widest text-slate-500 hover:text-amber-300">Ver tudo →</Link>
    </div>
    <ol className="space-y-2">
      {(ranking || []).slice(0, 5).map((o) => (
        <li key={o.id} data-testid={`top-ranking-row-${o.name}`} className="flex items-center gap-3 border border-slate-800/80 bg-slate-950/50 px-3 py-2.5">
          <span className={`w-6 font-mono text-sm font-bold ${o.position === 1 ? "text-amber-400" : "text-slate-500"}`}>#{o.position}</span>
          <span className="flex-1 font-medium">{o.label}</span>
          <span className="font-mono text-xs text-slate-500">{o.zones_held} zona(s)</span>
          <span className="w-16 text-right font-mono font-bold text-emerald-400">{o.points} pts</span>
        </li>
      ))}
    </ol>
  </div>
);

const ZonesSummary = ({ zones }) => (
  <div className="panel p-5" data-testid="dashboard-zones-summary">
    <h2 className="mb-4 text-lg font-semibold uppercase tracking-wider">Zonas</h2>
    <div className="grid gap-3 md:grid-cols-2">
      {(zones || []).map((z) => (
        <div key={z.id} data-testid={`zone-summary-${z.id}`} className="flex items-center justify-between border border-slate-800/80 bg-slate-950/50 px-4 py-3">
          <div>
            <div className="font-medium">{z.name} {!z.active && <span className="ml-2 font-mono text-[10px] uppercase text-slate-500">(inativa)</span>}</div>
            <div className="font-mono text-xs text-slate-500">{fmtCoords(z.coords)}</div>
          </div>
          <div className="text-right">
            <div className={`text-sm ${z.owner ? "text-emerald-300" : "text-slate-500"}`}>{z.owner?.label || "Sem dono"}</div>
            {z.active_war && <StatusBadge status={z.active_war.status} />}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const SuspiciousCard = ({ count = 0 }) => (
  <Link to="/auditoria" data-testid="dashboard-suspicious-card"
    className={`panel block p-5 transition-colors ${count > 0 ? "border-rose-500/50 bg-rose-950/30 hover:bg-rose-900/30" : "hover:bg-slate-900/60"}`}>
    <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-slate-500">
      <AlertTriangle className={`h-3.5 w-3.5 ${count > 0 ? "text-rose-400" : ""}`} />Suspeitos (24h)
    </div>
    <div data-testid="stat-suspicious-24h" className={`mt-2 font-display text-3xl font-bold ${count > 0 ? "text-rose-400" : "text-slate-50"}`}>{count}</div>
  </Link>
);

export default function DashboardPage() {
  const wars = useApi("/wars/active", 10000);
  const zones = useApi("/zones", 15000);
  const ranking = useApi("/ranking", 15000);
  const audit = useApi("/admin/audit/summary", 30000);
  const [cancelling, setCancelling] = useState(null);

  const confirmCancel = async () => {
    try {
      await api.post(`/admin/wars/${cancelling.id}/force-cancel`);
      toast.success("Guerra cancelada.");
      wars.reload();
      zones.reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setCancelling(null);
  };

  const downloadScript = async () => {
    try {
      const { data } = await api.get("/download/resource", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "dz_domination.zip";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Script descarregado com API_URL e API_KEY já preenchidas (server/sv_config.lua).");
    } catch (e) {
      toast.error(errMsg(e, "Não foi possível descarregar o script."));
    }
  };
  const zl = zones.data || [];
  const active = wars.data || [];
  return (
    <div>
      <PageHeader eyebrow="Centro de operações" title="Painel de Controlo">
        <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-emerald-400"><Radio className="h-3.5 w-3.5 animate-pulse" />Ao vivo</span>
        <button type="button" onClick={downloadScript} data-testid="download-fivem-script-button"
          className="flex items-center gap-2 border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 font-mono text-xs uppercase tracking-widest text-emerald-300 transition-colors hover:bg-emerald-500/20">
          <Download className="h-3.5 w-3.5" />Descarregar script FiveM
        </button>
      </PageHeader>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        <Stat label="Guerras ativas" value={active.length} accent="text-rose-400" testid="stat-active-wars" />
        <Stat label="Zonas" value={zl.length} testid="stat-zones-total" />
        <Stat label="Zonas dominadas" value={zl.filter((z) => z.owner).length} accent="text-emerald-400" testid="stat-zones-owned" />
        <Stat label="Organizações" value={(ranking.data || []).length} testid="stat-orgs-total" />
        <SuspiciousCard count={audit.data?.suspicious_24h} />
      </div>
      <div className="mt-8 grid gap-6 xl:grid-cols-3">
        <section className="xl:col-span-2">
          <h2 className="mb-4 text-lg font-semibold uppercase tracking-wider">Guerras em curso</h2>
          {active.length === 0 ? (
            <div className="panel p-10 text-center font-mono text-sm text-slate-500" data-testid="no-active-wars">Nenhuma guerra ativa neste momento.</div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2" data-testid="active-wars-list">
              {active.map((w) => <WarCard key={w.id} war={w} onCancel={setCancelling} />)}
            </div>
          )}
        </section>
        <div className="space-y-6">
          <ActiveCaptures />
          <TopRanking ranking={ranking.data} />
        </div>
      </div>
      <div className="mt-6"><ZonesSummary zones={zl} /></div>
      <ConfirmDialog open={!!cancelling} onOpenChange={(o) => !o && setCancelling(null)} testid="dashboard-cancel-war-dialog"
        title="Cancelar guerra?" confirmLabel="Cancelar guerra" onConfirm={confirmCancel}
        description={`A guerra em ${cancelling?.zone?.name || ""} será cancelada sem atribuição de pontos.`} />
    </div>
  );
}
